import * as React from "react";
import { mergeClasses } from "@fluentui/react-components";
import {
  Box20Regular,
  Briefcase20Regular,
  BuildingRetail20Regular,
  ChevronRight20Regular,
  ShieldTask20Regular,
  VehicleTruck20Regular,
} from "@fluentui/react-icons";
import { sectionColorHex } from "../risikoanalyseStep/sectionColor";
import { useTransportStyles } from "./sharedStyles";
import { FIELD_BY_KEY, TRANSPORT_VERTRAGSARTEN } from "./sections";
import { formatValue, hatDaten, vertragsartCounts } from "./values";
import type { TrValues } from "./types";

/**
 * Abschnitt 1 der Felddefinition — „Vertragsübersicht": alle Transport-
 * Vertragsarten mit ihren Eckdaten auf einen Blick. Von hier führt ein Klick
 * in die jeweilige Vertragsart.
 *
 * Welche Eckdaten eine Karte zeigt, steht je Vertragsart in `uebersicht`
 * (Spalte D des Excel-Abschnitts „Übersicht aller Vertragsarten": Summe,
 * Geltungsbereich, Selbstbehalt und je Art ein viertes Merkmal).
 */

const ICONS: Record<string, React.ReactNode> = {
  warentransport: <Box20Regular />,
  werkverkehr: <VehicleTruck20Regular />,
  frachtfuehrer: <ShieldTask20Regular />,
  messe: <BuildingRetail20Regular />,
  reisegepaeck: <Briefcase20Regular />,
};

type Props = {
  values: TrValues;
  onOpen: (vertragsartKey: string) => void;
};

const UebersichtPanel: React.FC<Props> = ({ values, onOpen }) => {
  const t = useTransportStyles();

  return (
    <div className={t.overviewGrid}>
      {TRANSPORT_VERTRAGSARTEN.map((art) => {
        const belegt = hatDaten(art, values);
        const { filled, total } = vertragsartCounts(art, values);
        const farbe = sectionColorHex[art.color];
        return (
          <button
            key={art.key}
            type="button"
            className={t.overviewCard}
            onClick={() => onOpen(art.key)}
          >
            <span className={t.overviewIcon} style={{ backgroundColor: farbe }}>
              {ICONS[art.key]}
            </span>
            <span className={t.overviewMain}>
              <span className={t.overviewTitle}>{art.title}</span>
              <span className={t.overviewStatus}>
                <span className={mergeClasses(t.overviewDot, belegt && t.overviewDotAktiv)} />
                {belegt ? `Erfasst — ${filled} von ${total} Feldern` : "Noch keine Angaben"}
              </span>
              <span className={t.overviewKpis}>
                {art.uebersicht.map((k) => {
                  const feld = FIELD_BY_KEY[k.fieldKey];
                  const wert = feld ? formatValue(feld, values) : "—";
                  return (
                    <span key={k.fieldKey} className={t.overviewKpi}>
                      <span className={t.overviewKpiLabel}>{k.label}</span>
                      <span className={t.overviewKpiValue} title={wert}>
                        {wert}
                      </span>
                    </span>
                  );
                })}
              </span>
            </span>
            <span className={t.overviewChevron}>
              <ChevronRight20Regular />
            </span>
          </button>
        );
      })}
    </div>
  );
};

export default UebersichtPanel;
